import type { StockDetailData, StockKlineBar, StockNotesTabProps } from "fin-web-components";
import { useCallback, useMemo, useState } from "react";
import type { StockNoteItem } from "../../products/user_holdings/UserStockNoteType";
import { useMyNotesNavigate } from "./useMyNotesNavigate";
import { useStockNotesTab } from "./useStockNotesTab";
import StockKlineApi from "./StockKlineApi";
import { queryStockDetailInfoForLongfans } from "./stockDetailQuery";

export type StockLinkDetailState = {
  code: string;
  data: StockDetailData | null;
  dayKlines: StockKlineBar[] | null;
  loading: boolean;
  klineLoading: boolean;
};

/**
 * 与 fin-web-components `StockLink` 配合：页面级缓存当前打开的标的详情、日线与笔记。
 * 笔记不依赖产品权益，登录用户在本空间即可使用。
 */
export function useStockLinkDetail(spaceId: string): {
  stockLinkDetail: StockLinkDetailState;
  requestStockDetailForLink: (stockCode: string) => void;
  buildStockNotesTabProps: (stockCode: string, stockName?: string) => StockNotesTabProps;
  loadPanel: (stockCode: string) => Promise<void>;
  listNotesForImport: (stockCode: string) => StockNoteItem[];
} {
  const { loadPanel, buildStockNotesTabProps, listNotesForImport } = useStockNotesTab(spaceId);
  const goMyNotes = useMyNotesNavigate(spaceId);

  const buildStockNotesTabPropsWithNav = useMemo(
    () =>
      (stockCode: string, stockName?: string): StockNotesTabProps => ({
        ...buildStockNotesTabProps(stockCode, stockName),
        onViewAllNotes: () => goMyNotes(stockCode),
      }),
    [buildStockNotesTabProps, goMyNotes]
  );

  const [stockLinkDetail, setStockLinkDetail] = useState<StockLinkDetailState>({
    code: "",
    data: null,
    dayKlines: null,
    loading: false,
    klineLoading: false,
  });

  const requestStockDetailForLink = useCallback(
    (code: string) => {
      const c = code.trim();
      if (!c) {
        return;
      }
      void loadPanel(c);
      setStockLinkDetail((prev) => {
        if (prev.code === c && prev.loading) {
          return prev;
        }
        return {
          code: c,
          data: null,
          dayKlines: null,
          loading: true,
          klineLoading: true,
        };
      });
      void Promise.all([
        queryStockDetailInfoForLongfans(c),
        StockKlineApi.queryStockDayKlines(c),
      ]).then(([data, dayKlines]) => {
        setStockLinkDetail((prev) =>
          prev.code === c
            ? { code: c, data, dayKlines, loading: false, klineLoading: false }
            : prev
        );
      });
    },
    [loadPanel]
  );

  return {
    stockLinkDetail,
    requestStockDetailForLink,
    buildStockNotesTabProps: buildStockNotesTabPropsWithNav,
    loadPanel,
    listNotesForImport,
  };
}
